import React, { useEffect } from 'react';
import { 
  X, 
  Download, 
  TrendingUp, 
  Calendar
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import { DatePreset } from '../../types/admin';

export interface AnalyticMetricDetails {
  id: string;
  title: string;
  value: string;
  subtitle: string;
  trend?: { value: string; positive: boolean };
  badge?: string;
  category: 'financial' | 'operational' | 'sustainability' | 'tribunal';
}

interface AnalyticBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  metric: AnalyticMetricDetails | null;
  timeframeLabel: string;
  datePreset: DatePreset;
}

export const AnalyticBreakdownModal: React.FC<AnalyticBreakdownModalProps> = ({
  isOpen,
  onClose,
  metric,
  timeframeLabel,
  datePreset,
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !metric) return null;

  // Generate dynamic contextual breakdown data based on metric category & id
  const getContextualBreakdown = () => {
    if (metric.id.includes('gmv') || metric.id.includes('revenue')) {
      return {
        dailyAvg: '₦46,250 / day',
        peakDay: 'Saturday (₦280,000)',
        totalCount: '142 settled checkouts',
        efficiencyRate: '98.6% settlement rate',
        series: [
          { day: 'Day 1', primary: 22000, secondary: 1770 },
          { day: 'Day 5', primary: 48000, secondary: 3870 },
          { day: 'Day 10', primary: 95000, secondary: 7660 },
          { day: 'Day 15', primary: 140000, secondary: 11280 },
          { day: 'Day 20', primary: 210000, secondary: 16930 },
          { day: 'Day 25', primary: 310000, secondary: 24990 },
          { day: 'Today', primary: 435000, secondary: 35070 },
        ],
        segments: [
          { label: 'Consumer Electronics & Phones', pct: '44%', val: '₦625,000', color: 'bg-emerald-400' },
          { label: 'Pre-Loved Designer & Fashion', pct: '28%', val: '₦398,000', color: 'bg-teal-400' },
          { label: 'Refurbished Home & Furniture', pct: '18%', val: '₦255,000', color: 'bg-cyan-400' },
          { label: 'Books, Hobbies & Collectibles', pct: '10%', val: '₦142,000', color: 'bg-amber-400' },
        ],
        transactions: [
          { ref: 'TXN-9842', desc: 'iPhone 13 Pro 128GB (Pristine)', date: 'Today, 14:22', amount: '₦340,000', status: 'Settled' },
          { ref: 'TXN-9839', desc: 'Sony WH-1000XM4 Headphones', date: 'Yesterday, 18:10', amount: '₦125,000', status: 'Settled' },
          { ref: 'TXN-9835', desc: 'Solid Oak Writing Desk', date: '2 days ago', amount: '₦85,000', status: 'Released' },
          { ref: 'TXN-9831', desc: 'Vintage Levi\'s Denim Jacket', date: '3 days ago', amount: '₦24,000', status: 'Settled' },
        ]
      };
    } else if (metric.id.includes('escrow')) {
      return {
        dailyAvg: '₦31,600 held avg',
        peakDay: 'Friday (₦140,000 lock)',
        totalCount: '18 active custody locks',
        efficiencyRate: '100% solvency guaranteed',
        series: [
          { day: 'Day 1', primary: 45000, secondary: 3000 },
          { day: 'Day 5', primary: 70000, secondary: 4200 },
          { day: 'Day 10', primary: 88000, secondary: 5600 },
          { day: 'Day 15', primary: 110000, secondary: 7800 },
          { day: 'Day 20', primary: 145000, secondary: 9900 },
          { day: 'Day 25', primary: 180000, secondary: 12400 },
          { day: 'Today', primary: 210000, secondary: 15200 },
        ],
        segments: [
          { label: 'Funds Awaiting Merchant Dispatch', pct: '35%', val: '₦73,500', color: 'bg-emerald-400' },
          { label: 'In-Transit / Courier Custody', pct: '40%', val: '₦84,000', color: 'bg-sky-400' },
          { label: '48-Hour Buyer Inspection Window', pct: '20%', val: '₦42,000', color: 'bg-teal-400' },
          { label: 'Under Formal Tribunal Review', pct: '5%', val: '₦10,500', color: 'bg-rose-400' },
        ],
        transactions: [
          { ref: 'ESC-4410', desc: 'MacBook Air M1 256GB - Transit to Abuja', date: 'Dispatched 4h ago', amount: '₦420,000', status: 'In Transit' },
          { ref: 'ESC-4408', desc: 'DJI Mini 3 Pro Drone - Inspection Stage', date: 'Delivered 18h ago', amount: '₦310,000', status: '48h Window' },
          { ref: 'ESC-4402', desc: 'Vintage Herman Miller Chair', date: 'Awaiting dispatch', amount: '₦145,000', status: 'Awaiting Pick' },
          { ref: 'ESC-4399', desc: 'Nintendo Switch OLED Bundle', date: 'Delivered 32h ago', amount: '₦180,000', status: '48h Window' },
        ]
      };
    } else if (metric.id.includes('carbon') || metric.id.includes('co2')) {
      return {
        dailyAvg: '3.8 kg CO2 / day',
        peakDay: 'Weekend Spike (+24 kg)',
        totalCount: '98 items recirculated',
        efficiencyRate: 'Equivalent to 5.2 metric tons ytd',
        series: [
          { day: 'Day 1', primary: 12, secondary: 1 },
          { day: 'Day 5', primary: 28, secondary: 2 },
          { day: 'Day 10', primary: 46, secondary: 4 },
          { day: 'Day 15', primary: 68, secondary: 6 },
          { day: 'Day 20', primary: 84, secondary: 8 },
          { day: 'Day 25', primary: 102, secondary: 9 },
          { day: 'Today', primary: 115, secondary: 11 },
        ],
        segments: [
          { label: 'Smartphones & Laptops (Refurbished)', pct: '52%', val: '59.8 kg CO2', color: 'bg-emerald-400' },
          { label: 'Fashion & Textile Reuse', pct: '26%', val: '29.9 kg CO2', color: 'bg-teal-400' },
          { label: 'Home Appliances & Furniture', pct: '15%', val: '17.2 kg CO2', color: 'bg-lime-400' },
          { label: 'Packaging Waste Avoidance', pct: '7%', val: '8.1 kg CO2', color: 'bg-cyan-400' },
        ],
        transactions: [
          { ref: 'ECO-102', desc: 'Apple MacBook Pro Recirculation', date: 'Today', amount: '18.4 kg CO2e saved', status: 'Verified' },
          { ref: 'ECO-099', desc: 'Samsung Galaxy S22 Direct Handoff', date: 'Yesterday', amount: '12.1 kg CO2e saved', status: 'Verified' },
          { ref: 'ECO-095', desc: 'Designer Leather Boots Re-Use', date: '2 days ago', amount: '4.8 kg CO2e saved', status: 'Verified' },
          { ref: 'ECO-091', desc: 'Solid Wood Dining Set', date: '3 days ago', amount: '22.6 kg CO2e saved', status: 'Verified' },
        ]
      };
    } else {
      // General / Tribunal case breakdown
      return {
        dailyAvg: '1.2 events / day',
        peakDay: 'Mid-week review',
        totalCount: '16 total cases processed',
        efficiencyRate: '94.2% SLA adherence',
        series: [
          { day: 'Day 1', primary: 1, secondary: 0 },
          { day: 'Day 5', primary: 3, secondary: 1 },
          { day: 'Day 10', primary: 5, secondary: 2 },
          { day: 'Day 15', primary: 8, secondary: 4 },
          { day: 'Day 20', primary: 11, secondary: 6 },
          { day: 'Day 25', primary: 14, secondary: 8 },
          { day: 'Today', primary: 16, secondary: 11 },
        ],
        segments: [
          { label: 'Item Condition Discrepancy', pct: '45%', val: '7 cases', color: 'bg-amber-400' },
          { label: 'Non-Delivery / Courier Delay', pct: '30%', val: '5 cases', color: 'bg-sky-400' },
          { label: 'Defective / Missing Accessories', pct: '15%', val: '2 cases', color: 'bg-rose-400' },
          { label: 'Mutual Cancellation & Goodwill', pct: '10%', val: '2 cases', color: 'bg-emerald-400' },
        ],
        transactions: [
          { ref: 'TRB-8821', desc: 'Samsung Monitor scratches - Partial refund', date: 'Today, 11:30', amount: '₦35,000 refund', status: 'Resolved' },
          { ref: 'TRB-8819', desc: 'Sony Camera lens cap missing - Escrow released', date: 'Yesterday', amount: '₦12,000 credit', status: 'Settled' },
          { ref: 'TRB-8814', desc: 'Courier non-arrival in Surulere', date: '3 days ago', amount: 'Full refund', status: 'Closed' },
        ]
      };
    }
  };

  const data = getContextualBreakdown();

  const handleExportCSV = () => {
    const csvHeader = 'Reference,Description,Timestamp,Value,Status\n';
    const csvRows = data.transactions.map(t => `"${t.ref}","${t.desc}","${t.date}","${t.amount}","${t.status}"`).join('\n');
    const blob = new Blob([csvHeader + csvRows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `reuseit_${metric.id}_${datePreset}_breakdown.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/70 backdrop-blur-md animate-fadeIn">
      <div 
        className="relative w-full max-w-4xl max-h-[90vh] flex flex-col rounded-3xl overflow-hidden border shadow-2xl transition-all
          bg-[#10231d] border-[#22493c] text-[#f5f5f0]
          light:bg-[#F4FBF7] light:border-[#CDE3D5] light:text-[#0D1C17] light:shadow-2xl"
      >
        {/* Modal Top Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#22493c] light:border-[#CDE3D5] bg-[#152a23] light:bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-brand-500/15 border border-brand-500/30 flex items-center justify-center text-brand-400 light:text-emerald-700 light:bg-emerald-100">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-extrabold text-lg sm:text-xl text-white light:text-[#0D1C17] tracking-tight">
                  {metric.title}
                </h3>
                {metric.badge && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-500/20 text-brand-300 border border-brand-500/30 light:bg-emerald-100 light:text-emerald-800">
                    {metric.badge}
                  </span>
                )}
              </div>
              <p className="text-xs text-[#90cbb7] light:text-[#527063] flex items-center gap-1.5 mt-0.5">
                <Calendar className="w-3.5 h-3.5" />
                <span>Duration: <strong className="font-semibold text-white light:text-[#0D1C17]">{timeframeLabel}</strong></span>
                <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-white/10 light:bg-emerald-50 text-brand-300 light:text-emerald-700">
                  {datePreset.toUpperCase()}
                </span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleExportCSV}
              className="hidden sm:flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition-all
                bg-[#22493c] hover:bg-[#2c5b4d] text-white border border-brand-500/30
                light:bg-white light:border-[#CDE3D5] light:text-[#0D1C17] light:hover:bg-emerald-50 shadow-sm"
              title="Export filtered records to CSV"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Export CSV</span>
            </button>

            <button
              onClick={onClose}
              className="p-2 rounded-full transition-colors text-[#90cbb7] hover:text-white hover:bg-white/10 light:text-[#527063] light:hover:bg-black/5"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Scrollable Body */}
        <div className="overflow-y-auto p-6 space-y-6 flex-1">
          {/* Top Aggregated Highlight Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
            <div className="p-4 rounded-2xl bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#CDE3D5]">
              <div className="text-[11px] font-semibold text-[#90cbb7] light:text-[#527063] uppercase tracking-wider">
                Current Total
              </div>
              <div className="text-xl sm:text-2xl font-black text-white light:text-[#0D1C17] mt-1 truncate">
                {metric.value}
              </div>
              {metric.trend && (
                <div className={`text-[11px] font-bold mt-1 flex items-center gap-1 ${metric.trend.positive ? 'text-emerald-400 light:text-emerald-600' : 'text-rose-400'}`}>
                  {metric.trend.positive ? '↑ ' : '↓ '} {metric.trend.value} vs prev. period
                </div>
              )}
            </div>

            <div className="p-4 rounded-2xl bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#CDE3D5]">
              <div className="text-[11px] font-semibold text-[#90cbb7] light:text-[#527063] uppercase tracking-wider">
                Daily Average
              </div>
              <div className="text-xl sm:text-2xl font-black text-white light:text-[#0D1C17] mt-1 truncate">
                {data.dailyAvg}
              </div>
              <div className="text-[11px] text-[#90cbb7]/80 light:text-[#527063] mt-1">
                Uniform velocity rate
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#CDE3D5]">
              <div className="text-[11px] font-semibold text-[#90cbb7] light:text-[#527063] uppercase tracking-wider">
                Peak Activity
              </div>
              <div className="text-xl sm:text-2xl font-black text-white light:text-[#0D1C17] mt-1 truncate">
                {data.peakDay.split(' ')[0]}
              </div>
              <div className="text-[11px] text-brand-400 light:text-emerald-700 mt-1 font-medium truncate">
                {data.peakDay}
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#CDE3D5]">
              <div className="text-[11px] font-semibold text-[#90cbb7] light:text-[#527063] uppercase tracking-wider">
                Health & Efficiency
              </div>
              <div className="text-xl sm:text-2xl font-black text-emerald-400 light:text-emerald-600 mt-1 truncate">
                {data.efficiencyRate.split(' ')[0]}
              </div>
              <div className="text-[11px] text-[#90cbb7]/80 light:text-[#527063] mt-1 truncate">
                {data.efficiencyRate}
              </div>
            </div>
          </div>

          {/* Time Series Trajectory Chart */}
          <div className="p-5 rounded-2xl bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#CDE3D5]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="font-bold text-sm text-white light:text-[#0D1C17]">
                  Trajectory Trend Analysis
                </h4>
                <p className="text-xs text-[#90cbb7] light:text-[#527063]">
                  Progressive distribution across the selected {timeframeLabel} window
                </p>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-brand-400" />
                  <span className="text-[#90cbb7] light:text-[#527063]">Primary Run</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-teal-300" />
                  <span className="text-[#90cbb7] light:text-[#527063]">Baseline</span>
                </div>
              </div>
            </div>

            <div className="h-60 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.series}>
                  <defs>
                    <linearGradient id="modalGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0df2a6" stopOpacity={0.4}/>
                      <stop offset="95%" stopColor="#0df2a6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#22493c" opacity={0.3} />
                  <XAxis dataKey="day" stroke="#90cbb7" fontSize={11} tickLine={false} />
                  <YAxis stroke="#90cbb7" fontSize={11} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#10231d', 
                      borderColor: '#22493c', 
                      borderRadius: '12px',
                      color: '#ffffff',
                      fontSize: '12px' 
                    }} 
                  />
                  <Area type="monotone" dataKey="primary" stroke="#0df2a6" strokeWidth={2.5} fillOpacity={1} fill="url(#modalGradient)" />
                  <Area type="monotone" dataKey="secondary" stroke="#2dd4bf" strokeWidth={1.5} fillOpacity={0} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Categorical Breakdown Splits */}
          <div className="p-5 rounded-2xl bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#CDE3D5]">
            <h4 className="font-bold text-sm text-white light:text-[#0D1C17] mb-3">
              Distribution by Segment / Category
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.segments.map((seg, idx) => (
                <div key={idx} className="p-3.5 rounded-xl bg-[#10231d] border border-[#22493c]/80 light:bg-[#F4FBF7] light:border-[#CDE3D5]">
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="font-semibold text-white light:text-[#0D1C17]">{seg.label}</span>
                    <span className="font-extrabold text-brand-400 light:text-emerald-700">{seg.pct}</span>
                  </div>
                  <div className="w-full bg-[#152a23] light:bg-gray-200 h-2 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full ${seg.color}`} 
                      style={{ width: seg.pct }}
                    />
                  </div>
                  <div className="mt-1.5 text-right text-[11px] text-[#90cbb7] light:text-[#527063]">
                    {seg.val}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sample Transactions / Log Entries */}
          <div className="p-5 rounded-2xl bg-[#152a23] border border-[#22493c] light:bg-white light:border-[#CDE3D5]">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold text-sm text-white light:text-[#0D1C17]">
                Contextual Activity Samples ({data.transactions.length})
              </h4>
              <span className="text-[11px] text-[#90cbb7] light:text-[#527063]">
                Verified against ledger
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-[#22493c] light:border-gray-200 text-[#90cbb7] light:text-[#527063]">
                    <th className="pb-2 font-semibold">Reference</th>
                    <th className="pb-2 font-semibold">Description</th>
                    <th className="pb-2 font-semibold">Timestamp</th>
                    <th className="pb-2 font-semibold text-right">Value</th>
                    <th className="pb-2 font-semibold text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#22493c]/50 light:divide-gray-100">
                  {data.transactions.map((t, idx) => (
                    <tr key={idx} className="hover:bg-white/5 light:hover:bg-emerald-50/50 transition-colors">
                      <td className="py-2.5 font-mono font-bold text-brand-400 light:text-emerald-700">{t.ref}</td>
                      <td className="py-2.5 font-medium text-white light:text-[#0D1C17]">{t.desc}</td>
                      <td className="py-2.5 text-[#90cbb7] light:text-[#527063]">{t.date}</td>
                      <td className="py-2.5 text-right font-bold text-white light:text-[#0D1C17]">{t.amount}</td>
                      <td className="py-2.5 text-right">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 light:bg-emerald-100 light:text-emerald-800">
                          {t.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Modal Bottom Actions */}
        <div className="px-6 py-4 border-t border-[#22493c] light:border-[#CDE3D5] bg-[#152a23] light:bg-white flex items-center justify-between shrink-0">
          <div className="text-xs text-[#90cbb7] light:text-[#527063]">
            Total records in sample: <strong>{data.transactions.length}</strong> • Filtered for {timeframeLabel}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportCSV}
              className="sm:hidden flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-[#22493c] text-white"
            >
              <Download className="w-3.5 h-3.5" />
              <span>CSV</span>
            </button>
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-xl text-xs font-bold bg-brand-500 hover:bg-brand-400 text-forest-void shadow-glow-sm transition-all"
            >
              Close Breakdown
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
